import { useCallback, useEffect, useRef, useState } from "react";
import type {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";
import { apiRequest } from "@/helper/api";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { buildListRequestParams } from "@/components/tables/genericCrudTable.listParams";
import { hasIncompleteMrtDateFilter } from "@/components/tables/genericCrudTableDateFilterIntegration";
import {
  filterCustomTableData,
  sortCustomTableData,
} from "@/components/tables/genericCrudTable.utils";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";

export interface TableExportParams {
  globalFilter?: string;
  columnFilters?: any[];
  additionalUrlParameters?: any;
  ordering?: string;
}

export interface UseTableFetchOptions {
  entity: string;
  entityAdapter?: EntityAdapter;
  columns: CrudColumnDef[];
  columnFilters: MRT_ColumnFiltersState;
  globalFilter: string;
  ordering: string;
  sorting: MRT_SortingState;
  pagination: MRT_PaginationState;
  searchId?: string;
  companySubjectId?: string;
  additionalUrlParameter?: Record<string, unknown>;
  additionalRoutePartsForGet?: Record<string, unknown>;
  additionalKeyForParameteresToRender?: string;
  expandedDepth?: boolean;
  shouldNotImpersonate?: boolean;
  refreshTable?: boolean;
  resetPaginationOnFilterChange?: boolean;
  onResetPagination?: () => void;
  customTableData?: any[];
  enableColumnFilteringWithCustomTableData?: boolean;
  enableSortingWithCustomTableData?: boolean;
  allowPaginationWithCustomTableData?: boolean;
  transformGetResults?: (results: any[]) => any[];
  onSuccessfulGet?: (data: any) => void;
  onExportParamsChange?: (params: TableExportParams) => void;
  onError: (error: any) => void;
  orderingReady?: boolean;
  reloadOn?: unknown[]; // forces a reload if value changes
}

export function useTableFetch(options: UseTableFetchOptions) {
  const [tableRows, setTableRows] = useState<any>([]);
  const [rowCount, setRowCount] = useState(0);
  const [emailsOnPage, setEmailsOnPage] = useState<string[]>([]); // feeds the uniqueness check of email columns with `validate`
  const [reloadToken, setReloadToken] = useState(0);

  const lastFetchKeyRef = useRef<string | null>(null);
  const activeFetchControllerRef = useRef<AbortController | null>(null);
  const latestFetchRequestIdRef = useRef(0);
  const previousPaginationResetKeyRef = useRef<string | null>(null);

  const invalidateActiveFetch = useCallback(() => {
    latestFetchRequestIdRef.current += 1;
    activeFetchControllerRef.current?.abort();
    activeFetchControllerRef.current = null;
  }, []);

  useEffect(
    () => () => {
      invalidateActiveFetch();
    },
    [invalidateActiveFetch]
  );

  const customTableData = options.customTableData;

  const requestReload = useCallback(() => {
    lastFetchKeyRef.current = null;
    if (!customTableData) {
      setTableRows([]);
    }
    setReloadToken((token) => token + 1);
  }, [customTableData]);

  const reloadOn = options.reloadOn ?? [];
  const isFirstReloadTriggerRunRef = useRef(true);
  useEffect(() => {
    if (isFirstReloadTriggerRunRef.current) {
      isFirstReloadTriggerRunRef.current = false;
      return;
    }
    requestReload();
  }, reloadOn);

  const {
    entity,
    columns,
    columnFilters,
    globalFilter,
    ordering,
    sorting,
    pagination,
    searchId,
    companySubjectId,
  } = options;

  useEffect(() => {
    if (options.resetPaginationOnFilterChange) {
      const resetKey = JSON.stringify({
        columnFilters,
        globalFilter,
        additionalUrlParameter: options.additionalUrlParameter,
        refreshTable: options.refreshTable,
      });
      const previousKey = previousPaginationResetKeyRef.current;
      previousPaginationResetKeyRef.current = resetKey;
      if (
        previousKey !== null &&
        previousKey !== resetKey &&
        pagination.pageIndex !== 0 &&
        options.onResetPagination
      ) {
        invalidateActiveFetch();
        options.onResetPagination();
        return;
      }
    }
    if (!customTableData) {
      if (options.orderingReady === false) return;
      const fetchKey = JSON.stringify({
        entity,
        searchId: searchId ?? null,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        globalFilter,
        columnSorting: ordering,
        columnFilter: columnFilters,
        companySubjectId: companySubjectId ?? null,
        refreshTable: options.refreshTable ?? null,
        additionalUrlParameter: options.additionalUrlParameter ?? null,
        additionalRoutePartsForGet: options.additionalRoutePartsForGet ?? null,
        shouldNotImpersonate: options.shouldNotImpersonate ?? false,
      });

      if (lastFetchKeyRef.current === fetchKey) return;

      lastFetchKeyRef.current = fetchKey;

      const hasIncompleteDateFilter = hasIncompleteMrtDateFilter(
        columnFilters,
        columns,
        options.entityAdapter?.allowsDateRangeFilters
      );
      if (hasIncompleteDateFilter) {
        invalidateActiveFetch();
        return;
      }

      activeFetchControllerRef.current?.abort();
      const requestId = latestFetchRequestIdRef.current + 1;
      latestFetchRequestIdRef.current = requestId;
      const requestController = new AbortController();
      activeFetchControllerRef.current = requestController;

      const fetchData = async () => {
        const {
          additionalUrlParameters,
          additionalRouteParts,
          parametersToRender,
        } = buildListRequestParams({
          columns,
          columnFilters,
          globalFilter,
          ordering,
          pagination,
          searchId,
          companySubjectId,
          additionalUrlParameter: options.additionalUrlParameter,
          additionalRoutePartsForGet: options.additionalRoutePartsForGet,
          additionalKeyForParameteresToRender:
            options.additionalKeyForParameteresToRender,
          expandedDepth: options.expandedDepth,
          entityAdapter: options.entityAdapter,
        });

        if (options.onExportParamsChange) {
          options.onExportParamsChange({
            globalFilter,
            columnFilters,
            additionalUrlParameters,
            ordering,
          });
        }

        try {
          const res: any = await apiRequest({
            method: "get",
            entity,
            additionalRouteParts,
            parametersToRender,
            shouldNotImpersonate: options.shouldNotImpersonate,
            skipGatewayRetry: options.entityAdapter?.skipGatewayRetry,
            config: { signal: requestController.signal },
          });
          if (
            requestController.signal.aborted ||
            requestId !== latestFetchRequestIdRef.current
          ) {
            return;
          }
          const data = res.data;
          const transformedResults = options.transformGetResults
            ? options.transformGetResults(data.results)
            : data.results;
          setTableRows(transformedResults);
          setRowCount(data.count);

          if (options.onSuccessfulGet) {
            options.onSuccessfulGet(data);
          }

          setEmailsOnPage(res.data.results.map((item: any) => item.email));
        } catch (error) {
          if (
            !requestController.signal.aborted &&
            requestId === latestFetchRequestIdRef.current
          ) {
            options.onError(error);
          }
        } finally {
          if (
            requestId === latestFetchRequestIdRef.current &&
            activeFetchControllerRef.current === requestController
          ) {
            activeFetchControllerRef.current = null;
          }
        }
      };
      fetchData();
    } else {
      invalidateActiveFetch();
      lastFetchKeyRef.current = null;
      let processedData = customTableData;

      if (
        options.enableColumnFilteringWithCustomTableData &&
        columnFilters.length > 0
      ) {
        processedData = filterCustomTableData(processedData, columnFilters, {
          columns,
          entityAdapter: options.entityAdapter,
        });
      }

      if (options.enableSortingWithCustomTableData && sorting.length > 0) {
        processedData = sortCustomTableData(processedData, sorting, {
          columns,
        });
      }

      if (options.allowPaginationWithCustomTableData) {
        const startIndex = pagination.pageIndex * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        setTableRows(processedData.slice(startIndex, endIndex));
        setRowCount(processedData.length);
      } else {
        setTableRows(processedData);
      }
    }
  }, [
    entity,
    searchId,
    pagination.pageSize,
    pagination.pageIndex,
    globalFilter,
    ordering,
    columnFilters,
    reloadToken,
    customTableData,
    companySubjectId,
    options.orderingReady,
    options.refreshTable,
    options.resetPaginationOnFilterChange,
    options.additionalUrlParameter,
    options.additionalRoutePartsForGet,
    options.shouldNotImpersonate,
    invalidateActiveFetch,

    ...(options.enableSortingWithCustomTableData ? [sorting] : []),
    ...(options.enableColumnFilteringWithCustomTableData
      ? [columnFilters]
      : []),
  ]);

  return { tableRows, rowCount, emailsOnPage, requestReload };
}
